import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import PageCard from "../../components/ui/PageCard";
import DateTimeCalendar from "../../components/ui/DateTimeCalendar";
import MagazinePageHeader from "../magazine/MagazinePageHeader";
import { ContactNote, createContactNote, deleteContactNote, getContact, updateContact, updateContactNote } from "../../api/contactsService";
import { useAuth } from "../../hooks/useAuth";

const toLocal = (value?: string | null) => value ? String(value).slice(0, 16) : "";
const displayDate = (value?: string | null) => value ? new Date(value).toLocaleString("en-GB") : "No reminder set";
const initials = (value?: string) => (value || "Unknown").split(/\s+|@/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("");

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEditContacts } = useAuth();
  const [contact, setContact] = useState<any>(null);
  const [contactNotes, setContactNotes] = useState<ContactNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderDirty, setReminderDirty] = useState(false);
  const [reminderError, setReminderError] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editNoteBody, setEditNoteBody] = useState("");
  const [remindAt, setRemindAt] = useState("");
  const [details, setDetails] = useState({ company_id: "", first_name: "", last_name: "", email: "", phone: "", job_title: "", is_primary: false });

  const populate = (data: any) => {
    const record = data?.contact || data;
    setContact(record || null);
    if (Array.isArray(data?.notes)) setContactNotes(data.notes);
    
    setRemindAt(toLocal(record?.remind_at));
    setDetails({ company_id: String(record?.company_id || record?.company?.id || ""), first_name: record?.first_name || "", last_name: record?.last_name || "", email: record?.email || "", phone: record?.phone || "", job_title: record?.job_title || "", is_primary: Boolean(record?.is_primary) });
  };

  useEffect(() => {
    getContact(Number(id)).then((data) => {
      populate(data);
      setLoading(false);
    });
  }, [id]);

  const saveDetails = async () => {
    if (!contact || !details.company_id || !details.first_name) return;
    setSaving(true);
    const updated = await updateContact(
      contact.id,
      {
        company_id: Number(details.company_id),
        first_name: details.first_name,
        last_name: details.last_name || null,
        email: details.email || null,
        phone: details.phone || null,
        job_title: details.job_title || null,
        is_primary: details.is_primary
      }
    );
    if (updated) populate({ ...contact, ...(updated.contact || updated) });
    setSaving(false); setEditingDetails(false);
  };

  const cancelDetails = () => { populate(contact); setEditingDetails(false); };
  const cancelNotes = () => { setNotes(""); setEditingNotes(false); };
  const saveNotes = async () => {
    if (!notes.trim()) return;
    setSaving(true);
    await createContactNote(contact.id, notes.trim());
    const refreshed = await getContact(contact.id);
    if (refreshed) populate(refreshed);
    setNotes(""); setSaving(false); setEditingNotes(false);
  };

  const refreshContact = async () => {
    const refreshed = await getContact(contact.id);
    if (refreshed) populate(refreshed);
  };

  const saveReminder = async () => {
    if (remindAt && new Date(remindAt).getTime() <= Date.now()) { setReminderError("Select a valid reminder date and time in the future."); return; }
    setSaving(true);
    await updateContact(contact.id, { remind_at: remindAt ? new Date(remindAt).toISOString() : null });
    await refreshContact();
    setReminderDirty(false); setReminderError(null); setSaving(false);
  };

  const saveEditedNote = async () => {
    if (!editingNoteId || !editNoteBody.trim()) return;
    setSaving(true);
    await updateContactNote(contact.id, editingNoteId, editNoteBody.trim());
    await refreshContact();
    setEditingNoteId(null); setEditNoteBody(""); setSaving(false);
  };

  const removeNote = async (noteId: number) => {
    if (!window.confirm("Delete this note?")) return;
    setSaving(true);
    await deleteContactNote(contact.id, noteId);
    await refreshContact();
    setSaving(false);
  };

  if (loading) return <Layout><PageCard><p className="text-sm text-gray-500">Loading contact...</p></PageCard></Layout>;
  if (!contact) return <Layout><PageCard><p className="text-sm text-red-600">Contact could not be found.</p><button onClick={() => navigate(-1)} className="mt-3 text-sm text-[#5949d1]">Go back</button></PageCard></Layout>;
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ");
  const author = "Unknown";
  const activities = contactNotes.filter((item) => item?.body);
  const companyLogo = contact.company?.logo ? `${process.env.REACT_APP_SERVER_URL}/api/${contact.company.logo}` : "";

  return <Layout>
    <div className="mx-auto max-w-6xl pb-8">
      <MagazinePageHeader
        title={name}
        description={`${contact.job_title || "Contact"}${contact.company?.name ? ` at ${contact.company.name}` : ""}`}
        action={<Link
                  to="/crm/contacts"
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 no-underline hover:bg-gray-50"
                >
                  Back to contacts
                </Link>
              }
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-1">
          <PageCard>
            <h2 className="text-base font-semibold text-gray-900">
              Company information
            </h2>
            <div className="mt-4">
              <div>
                {companyLogo && <div className="mb-4 flex h-24 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-2"><img src={companyLogo} alt={`${contact.company?.name || "Company"} logo`} className="max-h-full max-w-full object-contain" /></div>}
                <dl className="space-y-4 text-sm">
                  <Detail label="Company" value={contact.company?.name} />
                  <Detail label="Description" value={contact.company?.description} />
                </dl>
              </div>
            </div>
          </PageCard>

          <PageCard>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-gray-900">
                Contact details
              </h2>
              {(canEditContacts && !editingDetails)&&
                <button
                  onClick={() => setEditingDetails(true)}
                  className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Edit
                </button>
              }
            </div>
            {editingDetails
              ? <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="First name">
                      <div className="flex">
                        <input
                          value={details.first_name}
                          onChange={(e) => setDetails({ ...details, first_name: e.target.value })}
                          className="flex-1 field"
                        />
                      </div>
                    </Field>
                    <Field label="Last name">
                      <div className="flex">
                        <input
                          value={details.last_name}
                          onChange={(e) => setDetails({ ...details, last_name: e.target.value })}
                          className="flex-1 field"
                        />
                      </div>
                    </Field>
                  </div>
                  <Field label="Email">
                    <div className="flex">
                      <input
                        type="email"
                        value={details.email}
                        onChange={(e) => setDetails({ ...details, email: e.target.value })}
                        className="field"
                      />
                    </div>
                  </Field>
                  <Field label="Phone">
                    <div className="flex">
                      <input
                        value={details.phone}
                        onChange={(e) => setDetails({ ...details, phone: e.target.value })}
                        className="field"
                      />
                    </div>
                  </Field>
                  <Field label="Job title">
                    <div className="flex">
                      <input
                        value={details.job_title}
                        onChange={(e) => setDetails({ ...details, job_title: e.target.value })}
                        className="field"
                      />
                    </div>
                  </Field>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={details.is_primary}
                      onChange={(e) => setDetails({ ...details, is_primary: e.target.checked })}
                    />Primary contact
                  </label>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={cancelDetails}
                      className="rounded border border-gray-300 px-3 py-2 text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveDetails}
                      disabled={saving || !details.company_id || !details.first_name}
                      className="rounded bg-[#5949d1] px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                    >
                      {saving ? "Saving..." : "Save details"}
                    </button>
                  </div>
                </div>
              : <dl className="mt-4 space-y-4 text-sm">
                  <Detail label="Name" value={name} />
                  <Detail label="Email" value={contact.email} mail />
                  <Detail label="Phone" value={contact.phone} />
                  <Detail label="Job title" value={contact.job_title} />
                  <Detail label="Primary contact" value={contact.is_primary ? "Yes" : "No"} />
                </dl>
            }
          </PageCard>
        </div>

        <PageCard className="lg:col-span-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Notes &amp; follow-up
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Meeting, discussion and action record.
              </p>
            </div>
            {(canEditContacts && !editingNotes)&&
              <button
                onClick={() => setEditingNotes(true)}
                className="rounded bg-[#5949d1] px-4 py-2 text-sm font-medium text-white"
              >
                Add New
              </button>
            }
          </div>
          <div className="mt-5 rounded-lg border border-blue-100 bg-blue-50 p-4">
            <div className="flex flex-wrap items-center gap-2"><button onClick={() => setReminderOpen(true)} className="rounded border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100">{remindAt ? "Change reminder" : "Set reminder"}</button>{remindAt && <><button onClick={() => { setRemindAt(""); setReminderDirty(true); setReminderError(null); }} className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700">Clear reminder</button><span className={`rounded px-2 py-1 text-xs font-medium ${new Date(remindAt).getTime() < Date.now() ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>{new Date(remindAt).getTime() < Date.now() ? "Passed" : "Upcoming"} · {displayDate(remindAt)}</span></>}</div>
            {!remindAt && !reminderDirty && <p className="mt-2 text-sm text-blue-800">No reminder set.</p>}
            {reminderDirty && <button onClick={saveReminder} disabled={saving} className="mt-3 rounded bg-[#5949d1] px-3 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? "Saving..." : "Save reminder"}</button>}
            {reminderError && <p className="mt-2 text-sm text-red-600">{reminderError}</p>}
          </div>
          {editingNotes && <div className="mt-5 space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Add note</label>
                  <div className="flex">
                    <textarea
                      rows={7}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="flex-1 rounded-md border border-gray-300 p-3 text-sm"
                      placeholder="Record a meeting, discussion or action..."
                    />
                  </div>
                </div>
                <div className="hidden border-t border-gray-200 pt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setReminderOpen(true)}
                      className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100"
                    >
                      {remindAt ? "Change reminder" : "Set reminder"}
                    </button>
                    {remindAt&& 
                      <>
                        <button
                          onClick={() => { setRemindAt(""); setReminderDirty(true); setReminderError(null); }}
                          className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700"
                        >
                          Clear reminder
                        </button>
                        <span
                          className={`rounded px-2 py-1 text-xs font-medium ${new Date(remindAt).getTime() < Date.now() ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}
                        >
                          {new Date(remindAt).getTime() < Date.now() ? "Passed" : "Upcoming"} · {displayDate(remindAt)}
                        </span>
                      </>
                    }
                  </div>
                  {(reminderDirty && !reminderError)&&
                    <p className="mt-2 text-xs text-gray-500">
                      Click Save record to apply this reminder.
                    </p>
                  }
                  {reminderError&& <p className="mt-2 text-sm text-red-600">{reminderError}</p>}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={cancelNotes}
                    className="rounded border border-gray-300 px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveNotes}
                    disabled={saving || !notes.trim()}
                    className="rounded bg-[#5949d1] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                  >
                    {saving ? "Adding..." : "Add note"}
                  </button>
                </div>
              </div>}
            {<div className="mt-5">
                <div className="hidden mb-5 rounded-lg border border-blue-100 bg-blue-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Next reminder</p>
                  <p className="mt-1 font-medium text-blue-950">{displayDate(contact.remind_at)}</p>
                </div>
                {activities.length
                  ? <ul className="space-y-4">
                    {activities.map((item: any, index: number) =>
                      <li
                        key={item.id || index}
                        className="flex gap-3 border-b border-gray-100 pb-4"
                      >
                        {/* <Avatar value={item.author?.username || item.author?.email} /> */}
                        <div className="min-w-0 flex-1">
                          {editingNoteId === item.id
                            ? <div><textarea rows={3} value={editNoteBody} onChange={(e) => setEditNoteBody(e.target.value)} className="w-full rounded-md border border-gray-300 p-2 text-sm" /><div className="mt-2 flex gap-2"><button onClick={() => { setEditingNoteId(null); setEditNoteBody(""); }} className="rounded border border-gray-300 px-3 py-1.5 text-sm">Cancel</button><button onClick={saveEditedNote} disabled={saving || !editNoteBody.trim()} className="rounded bg-[#5949d1] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">Save</button></div></div>
                            : <p className="whitespace-pre-wrap text-sm text-gray-800">{item.body}</p>}
                          <p className="mt-1 text-xs text-gray-500">
                            {item.author?.username || item.author?.email || "Unknown"} · {displayDate(item.createdAt || item.created_at)}
                          </p>
                          
                        </div>
                        {(canEditContacts && editingNoteId !== item.id)&&
                          <div className="mt-2 flex items-center gap-3">
                            <button
                              onClick={() => {
                                setEditingNoteId(item.id);
                                setEditNoteBody(item.body);
                              }}
                              className="text-sm px-2 font-medium text-[#5949d1] hover:underline"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => removeNote(item.id)}
                              disabled={saving}
                              className="text-sm px-2 font-medium text-red-600 hover:underline disabled:opacity-50"
                            >
                              Delete
                            </button>
                          </div>
                        }
                      </li>
                    )}
                    </ul>
                  : false
                    ? <div className="flex gap-3">
                        <Avatar value={author} />
                        <div>
                          <p className="whitespace-pre-wrap text-sm text-gray-800">
                            {""}
                          </p>
                          <p className="mt-2 text-xs text-gray-500">
                            Last updated by {author}{contact.updatedAt ? ` · ${displayDate(contact.updatedAt)}` : ""}
                          </p>
                        </div>
                      </div>
                    : <p className="rounded-md bg-gray-50 p-6 text-center text-sm text-gray-500">No contact notes yet.</p>
                }
              </div>
            }
        </PageCard>
      </div>
    </div>
    {reminderOpen&&
      <DateTimeCalendar
        value={remindAt}
        onClose={() => setReminderOpen(false)}
        onChange={(value) => { setRemindAt(value); setReminderDirty(true); setReminderError(null); }}
        disabled={saving}
      />
    }
  </Layout>;
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => 
  <label className="block text-sm text-gray-600">
    {label}
    <div className="mt-1 [&_.field]:w-full [&_.field]:rounded [&_.field]:border [&_.field]:border-gray-300 [&_.field]:px-3 [&_.field]:py-2 [&_.field]:text-gray-900">
      {children}
    </div>
  </label>;
const Detail = ({ label, value, mail }: { label: string; value?: string; mail?: boolean }) => 
  <div className="min-w-[320px]">
    <dt className="text-gray-500">{label}</dt>
    <dd className="mt-1 font-medium text-gray-900">
      {(mail && value)
        ? <a href={`mailto:${value}`} className="text-[#5949d1]">{value}</a>
        : value || "—"
      }
    </dd>
  </div>;
const Avatar = ({ value }: { value?: string }) =>
  <span
    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-semibold text-violet-700"
  >
    {initials(value)}
  </span>;

export default ContactDetail;
